/**
 * CreateActorWithAIDialog Component
 *
 * Dialog for creating an actor using AI.
 * Replaces legacy dialog from ProjectViewPage.js (lines 2592-2756).
 *
 * Features:
 * - Actor name input
 * - Target audience selection
 * - AI prompt with @ mentions (MentionPromptField)
 * - Source data selection (CompositeDataPicker)
 * - Validation and error display
 * - AI webhook submission
 */

import React, { useCallback, useEffect, useMemo } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { SDialog, SAccordion, StyledTextField } from '@modules/ui-components';
import { MentionPromptField } from '@modules/ui-components/components/MentionPromptField';
import type { MentionDataContext, UserData as MentionUserData, ProjectData as MentionProjectData } from '@modules/ui-components/components/MentionPromptField/types';
import { CompositeDataPicker } from '@modules/data-pickers';
import { useActorAICreation } from '../hooks/useActorAICreation';
import type { CreateActorWithAIDialogProps, ActorFormData } from '../types';

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * CreateActorWithAIDialog Component
 *
 * @param props - Component props
 * @returns React component
 */
export function CreateActorWithAIDialog(props: CreateActorWithAIDialogProps): React.ReactElement | null {
  const {
    open,
    onClose,
    projectId,
    currentUser,
    project,
    audiences,
    actorsByAudience,
    tasks,
    studyCases,
    onSuccess,
    onError,
    showNotification,
  } = props;

  // ===========================================================================
  // HOOK
  // ===========================================================================

  /**
   * Wrapped onSuccess that closes dialog after success
   */
  const handleSuccess = useCallback(() => {
    onSuccess?.();
    onClose();
  }, [onSuccess, onClose]);

  const {
    formData,
    setFormData,
    errors,
    selectedPaths,
    setSelectedPaths,
    hydratedSourceData,
    setHydratedSourceData,
    setHydratedPrompt,
    handleSubmit,
    reset,
    isSubmitting,
    isValid,
  } = useActorAICreation({
    projectId,
    currentUser,
    project,
    audiences,
    actorsByAudience,
    tasks,
    studyCases,
    onSuccess: handleSuccess,
    onError,
    showNotification,
  });

  // ===========================================================================
  // TYPE CONVERSIONS (for MentionPromptField compatibility)
  // ===========================================================================

  /**
   * Convert currentUser to MentionPromptField's UserData format
   */
  const mentionUser: MentionUserData = useMemo(() => ({
    id: currentUser.uid,
    ...currentUser,
  }), [currentUser]);

  /**
   * Convert project to MentionPromptField's ProjectData format
   */
  const mentionProject: MentionProjectData | undefined = useMemo(() => {
    if (!project) return undefined;
    return {
      id: projectId,
      ...project,
    };
  }, [project, projectId]);

  /**
   * Convert data context for MentionPromptField
   * Uses type assertion since our types are structurally compatible but TypeScript
   * is strict about unknown vs specific types in nested fields
   */
  const mentionDataContext: MentionDataContext = useMemo(() => ({
    audiences: audiences as MentionDataContext['audiences'],
    actorsByAudience: actorsByAudience as MentionDataContext['actorsByAudience'],
    tasks: tasks as MentionDataContext['tasks'],
    studyCases: studyCases as MentionDataContext['studyCases'],
  }), [audiences, actorsByAudience, tasks, studyCases]);

  // ===========================================================================
  // EFFECTS
  // ===========================================================================

  /**
   * Reset form when dialog closes
   */
  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  // ===========================================================================
  // HANDLERS
  // ===========================================================================

  /**
   * Handle name change
   */
  const handleNameChange = useCallback(
    (value: string) => {
      setFormData((prev: ActorFormData) => ({ ...prev, name: value }));
    },
    [setFormData]
  );

  /**
   * Handle audience change
   */
  const handleAudienceChange = useCallback(
    (event: any) => {
      setFormData((prev: ActorFormData) => ({ ...prev, audienceId: event.target.value }));
    },
    [setFormData]
  );

  /**
   * Handle prompt change
   */
  const handlePromptChange = useCallback(
    (value: string) => {
      setFormData((prev: ActorFormData) => ({ ...prev, prompt: value }));
    },
    [setFormData]
  );

  /**
   * Handle cancel button click
   */
  const handleCancel = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  // ===========================================================================
  // RENDER
  // ===========================================================================

  return (
    <SDialog
      open={open}
      onClose={handleCancel}
      title="Create Actor with AI"
      maxWidth="md"
      fullWidth
      primaryAction={{
        label: 'Create',
        onClick: handleSubmit,
        disabled: !isValid || isSubmitting,
        loading: isSubmitting,
        variant: 'contained',
      }}
    >
      {/* Actor Name */}
      <StyledTextField
        id="actor-name"
        label="Actor Name"
        value={formData.name}
        onChange={handleNameChange}
        error={!!errors.name}
        helperText={errors.name}
        fullWidth
        margin="normal"
        disabled={isSubmitting}
      />

      {/* Target Audience */}
      <FormControl fullWidth margin="normal" error={!!errors.audienceId}>
        <InputLabel id="audience-select-label">Target Audience</InputLabel>
        <Select
          labelId="audience-select-label"
          id="audience-select"
          value={formData.audienceId}
          label="Target Audience"
          onChange={handleAudienceChange}
          disabled={isSubmitting}
        >
          {audiences.map((audience) => (
            <MenuItem key={audience.id} value={audience.id}>
              {audience.name}
            </MenuItem>
          ))}
        </Select>
        {errors.audienceId && <FormHelperText>{errors.audienceId}</FormHelperText>}
      </FormControl>

      {/* AI Prompt with Mentions */}
      <MentionPromptField
        label="AI Prompt"
        value={formData.prompt}
        onChange={handlePromptChange}
        projectId={projectId}
        currentUser={mentionUser}
        project={mentionProject}
        dataContext={mentionDataContext}
        onHydratedResultChange={setHydratedPrompt}
        placeholder="Describe the actor you want to create. Use @ to reference existing data..."
        error={!!errors.prompt}
        errorMessage={errors.prompt}
        disabled={isSubmitting}
      />

      {/* Data References Selection */}
      <SAccordion title="Select Data References" defaultExpanded={false}>
        <CompositeDataPicker
          projectId={projectId}
          savedSelections={selectedPaths}
          onSelectionChange={setSelectedPaths}
          onHydratedDataChange={setHydratedSourceData}
          pickers={[
            { type: 'audience', label: 'Audiences', enabled: true },
            { type: 'task', label: 'Tasks', enabled: true },
          ]}
          layout="tabs"
          height={400}
        />
      </SAccordion>
    </SDialog>
  );
}

export default CreateActorWithAIDialog;
